import React from "react";
import type {
  Block,
  ParagraphBlockData,
  HeadingBlockData,
  ImageBlockData,
  QuoteBlockData,
  CodeBlockData,
  DividerBlockData,
  EmbedBlockData,
  YouTubeBlockData,
  CalloutBlockData,
  TableBlockData,
} from "./blocks/types";

/**
 * Public-facing read-only block renderer for /voices posts.
 *
 * Self-contained — no dispatch to per-block components. All 10 block types
 * inlined here using dotperson's cream/serif palette + monospace metadata
 * accents. The block array shape matches HT.com's so future cross-port is
 * trivial.
 */

interface BlockRendererProps {
  blocks: Block[];
  className?: string;
}

function isEmpty(block: Block): boolean {
  const d = block.data as Record<string, unknown>;
  switch (block.type) {
    case "paragraph":
    case "heading":
    case "quote":
    case "callout":
    case "code":
      return !d.text && !d.code;
    case "image":
    case "embed":
    case "youtube":
      return !d.src && !d.url;
    case "table":
      return !Array.isArray(d.headers) || (d.headers as string[]).length === 0;
    default:
      return false;
  }
}

function extractYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/
  );
  return match ? match[1] : null;
}

function ParagraphBlockNode({ data }: { data: ParagraphBlockData }) {
  const align = data.align ?? "left";
  return (
    <p
      className="font-serif text-[1.0625rem] leading-[1.7] text-ink mb-5"
      style={{ textAlign: align }}
    >
      {data.text}
    </p>
  );
}

function HeadingBlockNode({ data }: { data: HeadingBlockData }) {
  const Tag = (`h${data.level}` as unknown) as React.ElementType;
  const sizes = ["text-3xl", "text-2xl", "text-xl", "text-lg", "text-base", "text-sm"];
  return (
    <Tag
      className={`font-serif font-medium tracking-tight text-ink mt-8 mb-4 ${sizes[data.level - 1] ?? "text-2xl"}`}
    >
      {data.text}
    </Tag>
  );
}

function ImageBlockNode({ data }: { data: ImageBlockData }) {
  const widthMap: Record<string, string> = {
    normal: "max-w-2xl",
    wide: "max-w-4xl",
    full: "max-w-none",
  };
  const widthClass = data.widthPct
    ? "max-w-none"
    : widthMap[data.width ?? "normal"];
  const widthStyle = data.widthPct ? { maxWidth: `${data.widthPct}%` } : undefined;
  return (
    <figure className={`mx-auto my-8 ${widthClass}`} style={widthStyle}>
      <img
        src={data.src}
        alt={data.alt}
        className="w-full h-auto block"
        loading="lazy"
      />
      {data.caption && (
        <figcaption className="text-center font-mono text-xs text-ink-3 mt-2 tracking-wide">
          {data.caption}
        </figcaption>
      )}
    </figure>
  );
}

function QuoteBlockNode({ data }: { data: QuoteBlockData }) {
  return (
    <blockquote className="border-l-2 border-claude pl-6 my-8">
      <p className="font-serif text-[1.1875rem] italic leading-relaxed text-ink m-0">
        {data.text}
      </p>
      {data.attribution && (
        <cite className="block mt-2 font-mono text-[13px] text-ink-3 not-italic">
          — {data.attribution}
        </cite>
      )}
    </blockquote>
  );
}

function CodeBlockNode({ data }: { data: CodeBlockData }) {
  return (
    <pre className="bg-paper-2 border border-hairline p-4 overflow-x-auto my-6">
      <code className="font-mono text-[13px] text-ink whitespace-pre-wrap">
        {data.code}
      </code>
    </pre>
  );
}

function DividerBlockNode({ data }: { data: DividerBlockData }) {
  if (data.style === "dots") {
    return (
      <div className="text-center py-8 text-ink-3" style={{ letterSpacing: "1em" }}>
        ···
      </div>
    );
  }
  if (data.style === "stars") {
    return (
      <div
        className="text-center py-8 text-claude"
        style={{ letterSpacing: "1em" }}
      >
        ∗∗∗
      </div>
    );
  }
  return <hr className="border-0 border-t border-hairline my-8" />;
}

function EmbedBlockNode({ data }: { data: EmbedBlockData }) {
  if (data.type === "youtube") {
    const id = extractYouTubeId(data.url);
    if (id) {
      return (
        <div className="relative w-full my-6" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            className="absolute inset-0 w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Embedded video"
          />
        </div>
      );
    }
  }
  return (
    <div className="bg-paper-2 border border-hairline p-4 my-6">
      <a
        href={data.url}
        className="font-mono text-sm text-claude no-underline hover:underline"
        target="_blank"
        rel="noreferrer"
      >
        View content →
      </a>
    </div>
  );
}

function YoutubeBlockNode({ data }: { data: YouTubeBlockData }) {
  const id = extractYouTubeId(data.url);
  if (!id) {
    return (
      <div className="bg-paper-2 border border-hairline p-4 my-6">
        <a
          href={data.url}
          className="font-mono text-sm text-claude no-underline hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Watch on YouTube →
        </a>
      </div>
    );
  }
  const params = new URLSearchParams();
  if (data.hideRelated) params.set("rel", "0");
  if (data.modestBranding) params.set("modestbranding", "1");
  if (data.startAt) params.set("start", String(data.startAt));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const widthStyle = data.widthPct ? { maxWidth: `${data.widthPct}%` } : undefined;
  return (
    <div
      className="relative mx-auto my-6"
      style={{ paddingBottom: "56.25%", height: 0, ...(widthStyle ?? {}) }}
    >
      <iframe
        src={`https://www.youtube.com/embed/${id}${qs}`}
        className="absolute inset-0 w-full h-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="YouTube video"
      />
    </div>
  );
}

function CalloutBlockNode({ data }: { data: CalloutBlockData }) {
  const labels: Record<string, string> = {
    info: "Note",
    warning: "Warning",
    success: "Confirmed",
    error: "Caution",
  };
  const label = labels[data.type ?? "info"];
  return (
    <aside className="bg-paper-2 border-l-2 border-claude px-5 py-4 my-6">
      <p className="font-mono text-[11px] text-claude uppercase tracking-widest m-0 mb-2">
        {label}
      </p>
      {data.title && (
        <h4 className="font-serif font-medium text-base text-ink m-0 mb-2">
          {data.title}
        </h4>
      )}
      <p className="font-serif text-[15px] leading-relaxed text-ink m-0">
        {data.text}
      </p>
    </aside>
  );
}

function TableBlockNode({ data }: { data: TableBlockData }) {
  return (
    <div className="overflow-x-auto my-6">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {data.headers.map((h, i) => (
              <th
                key={i}
                className="border border-hairline px-3 py-3 bg-paper-2 font-mono text-xs text-left tracking-wide text-ink"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, rIdx) => (
            <tr key={rIdx}>
              {row.map((cell, cIdx) => (
                <td
                  key={cIdx}
                  className="border border-hairline px-3 py-3 font-serif text-[15px] text-ink"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderBlock(block: Block): React.ReactNode {
  switch (block.type) {
    case "paragraph":
      return <ParagraphBlockNode data={block.data as ParagraphBlockData} />;
    case "heading":
      return <HeadingBlockNode data={block.data as HeadingBlockData} />;
    case "image":
      return <ImageBlockNode data={block.data as ImageBlockData} />;
    case "quote":
      return <QuoteBlockNode data={block.data as QuoteBlockData} />;
    case "code":
      return <CodeBlockNode data={block.data as CodeBlockData} />;
    case "divider":
      return <DividerBlockNode data={block.data as DividerBlockData} />;
    case "embed":
      return <EmbedBlockNode data={block.data as EmbedBlockData} />;
    case "youtube":
      return <YoutubeBlockNode data={block.data as YouTubeBlockData} />;
    case "callout":
      return <CalloutBlockNode data={block.data as CalloutBlockData} />;
    case "table":
      return <TableBlockNode data={block.data as TableBlockData} />;
    default:
      return null;
  }
}

export function BlockRenderer({ blocks, className = "" }: BlockRendererProps) {
  if (!blocks?.length) return null;
  return (
    <div className={className}>
      {blocks.map((block) => {
        if (isEmpty(block)) return null;
        const node = renderBlock(block);
        if (!node) return null;
        const wrapperStyle: React.CSSProperties = {};
        if (block.spacingAbove !== undefined) wrapperStyle.paddingTop = `${block.spacingAbove}px`;
        if (block.spacingBelow !== undefined) wrapperStyle.paddingBottom = `${block.spacingBelow}px`;
        return (
          <div
            key={block.id}
            style={Object.keys(wrapperStyle).length > 0 ? wrapperStyle : undefined}
          >
            {node}
          </div>
        );
      })}
    </div>
  );
}
